//Built-in libraries

import { PencilIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent } from 'react';
//Third party libraries
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { HasPermission } from '@/components/Permission/HasPermission';
import { CompanyAddress } from '@/components/Settings/CompanyProfile/CompanyAddress';
import { EditableView } from '@/components/Settings/CompanyProfile/EditableView';
import AccentTitle from '@/components/UI/AccentTitle';
import Badge from '@/components/UI/Badge';
import { PhoneNumberFormatDisplay } from '@/components/UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
import { patchSettingsAccountLogo } from '../../../api/settingsAccount';
import { ReactQueryKey } from '../../../constants/ReactQueryKeys';
import useSettingsAccount from '../../../hooks/ReactQueryHooks/useSettingsAccountQuery';
import { useUsers } from '../../../hooks/ReactQueryHooks/useUsersQuery';
//Custom components
import useUpload from '../../../hooks/useUpload';
//Services
import { authSelector } from '../../../store';
import { notifyUser } from '../../../store/Toast';
//Assets andd Miscellaneous
import type { User } from '../../../types/User';
import { sanitizeResponseError } from '../../../utils';
import { phoneRegEx, websiteRegex } from '../../../utils/RegularExpressions';

//Component Begin
export default function CompanyProfile() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data: currentUserData } = useSelector(authSelector);
  const { accountId, isSuper } = currentUserData;
  const hasValidAccountId = Number.isInteger(accountId) && accountId > 0;

  const fetchCurrentAccount = useSettingsAccount({ accountId, isSystemAdmin: isSuper, enabled: hasValidAccountId });
  const currentClientAccount = fetchCurrentAccount?.data?.data;

  const { uploadFile: uploadClientAccountLogo } = useUpload();

  const fetchUsers = useUsers({});
  const { mutate: updateClientAccountLogo } = useMutation(
    (data: unknown) => patchSettingsAccountLogo({ accountId, isSystemAdmin: isSuper }, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ReactQueryKey.GET_CLIENT_ACCOUNT]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Successfully updated logo',
            toastType: 'success',
          })
        );
      },
      onError: (error: unknown) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    uploadClientAccountLogo({
      file: file,
      resource: 'accounts',
      resourceId: accountId,
      applyData: updateClientAccountLogo,
    });
  };

  const renderUnavailableState = (message: string) => (
    <div className='px-6 py-6'>
      <div className='rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600'>{message}</div>
    </div>
  );

  if (!hasValidAccountId) {
    return (
      <div className='pb-5 divide-y divide-gray-200'>
        <AccentTitle title='Company Profile' />
        {renderUnavailableState('Account context is still loading. Please wait a moment and try again.')}
      </div>
    );
  }

  if (fetchCurrentAccount.isLoading) {
    return (
      <div className='pb-5 divide-y divide-gray-200'>
        <AccentTitle title='Company Profile' />
        {renderUnavailableState('Loading company profile...')}
      </div>
    );
  }

  if (fetchCurrentAccount.isError || !currentClientAccount) {
    return (
      <div className='pb-5 divide-y divide-gray-200'>
        <AccentTitle title='Company Profile' />
        {renderUnavailableState('Unable to load company profile for the selected account. Please refresh or switch accounts again.')}
      </div>
    );
  }

  return (
    <div className='pb-5 divide-y divide-gray-200'>
      <AccentTitle title='Company Profile' />
      <div className='px-6'>
        <dl className='divide-y divide-gray-200'>
          <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5'>
            <dt className='text-sm font-medium text-gray-500'>Logo</dt>
            <dd className='mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              <span className='flex-grow'>
                {currentClientAccount?.logoImageUrl && (
                  <img className='h-12 w-40' src={currentClientAccount?.logoImageUrl} alt='Company logo' />
                )}
                {!currentClientAccount?.logoImageUrl && (
                  <img
                    className='h-10 w-46'
                    src='https://assets.seinterminal.com/images/logos/SEIN_Tech_Partners_Logo_Color.png'
                    alt='sein technology partners logo'
                  />
                )}
              </span>
              <HasPermission of='UPDATE_COMPANY_PROFILE'>
                <span className='relative ml-4 shrink-0 flex items-start space-x-4 text-neutralColor-light hover:text-primaryColor-normal cursor-pointer'>
                  <PencilIcon className='h-5 w-5 ' />
                  <input
                    type='file'
                    name='profileImage'
                    className='absolute right-0 w-full h-full opacity-0 cursor-pointer border-gray-300 rounded-md'
                    accept='.jpg, .jpeg, .png, .svg'
                    onChange={handleFileChange}
                  />
                </span>
              </HasPermission>
            </dd>
          </div>
          <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4'>
            <dt className='text-sm font-medium text-gray-500'>Company name</dt>
            <dd className='mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              {currentClientAccount && (
                <EditableView
                  company={currentClientAccount}
                  name='name'
                  view={currentClientAccount?.name}
                  successMessage='Successfully updated Company Name'
                  validationSchema={Yup.object({
                    name: Yup.string()
                      .required('Required')
                      .min(2, 'This should be at least 2 character')
                      .max(50, 'This should not be more than 50 characters.'),
                  })}
                />
              )}
            </dd>
          </div>

          <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5'>
            <dt className='text-sm font-medium text-gray-500'>Subdomain</dt>
            <dd className='mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              <span className='flex-grow'>
                {currentClientAccount?.subdomain}
                .seinterminal.com
              </span>
            </dd>
          </div>
          <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5'>
            <dt className='text-sm font-medium text-gray-500'>Company address</dt>
            <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              {currentClientAccount && <CompanyAddress company={{ ...currentClientAccount }} />}
            </dd>
          </div>
          {currentClientAccount?.subdomain !== 'sageadmin' && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200'>
              <dt className='text-sm font-medium text-gray-500'>Point of contact (POC)</dt>
              <dd className='mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {currentClientAccount && (
                  <EditableView
                    company={currentClientAccount}
                    name='clientRep'
                    type='select'
                    defaultValue={{
                      clientRep: {
                        label: currentClientAccount.clientRep?.name,
                        value: currentClientAccount.clientRep?.id?.toString() ?? '',
                      },
                    }}
                    options={fetchUsers.data?.data.map((user: User) => ({
                      label: user.name,
                      value: user.id.toString(),
                    }))}
                    view={<> {currentClientAccount?.clientRep?.name}</>}
                    successMessage='Successfully updated Point of Contact'
                    validationSchema={Yup.object({
                      clientRep: Yup.object({
                        label: Yup.string().required('Required'),
                        value: Yup.string().required('Required'),
                      }),
                    })}
                  />
                )}
              </dd>
            </div>
          )}
          {currentClientAccount?.subdomain !== 'sageadmin' && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200'>
              <dt className='text-sm font-medium text-gray-500'>POC Email</dt>
              <dd className='mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <span className='flex-grow'>{currentClientAccount?.clientRep?.email}</span>
              </dd>
            </div>
          )}
          <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200'>
            <dt className='text-sm font-medium text-gray-500'>Phone number</dt>
            <dd className='mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              {currentClientAccount && (
                <EditableView
                  company={currentClientAccount}
                  name='companyPhone'
                  view={<PhoneNumberFormatDisplay value={currentClientAccount?.companyPhone} />}
                  successMessage='Successfully updated Phone Number'
                  type='masked'
                  maskType='phone'
                  validationSchema={Yup.object({
                    companyPhone: Yup.string().required('Required').matches(phoneRegEx, 'Invalid phone number'),
                  })}
                />
              )}
            </dd>
          </div>
          <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200'>
            <dt className='text-sm font-medium text-gray-500'>Company Website</dt>
            <dd className='mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              {currentClientAccount && (
                <EditableView
                  company={currentClientAccount}
                  name='companyWebsite'
                  maxLength={30}
                  minLength={6}
                  view={currentClientAccount?.companyWebsite}
                  successMessage='Successfully updated Company Website'
                  validationSchema={Yup.object({
                    companyWebsite: Yup.string().matches(websiteRegex, 'Invalid Website'),
                  })}
                />
              )}
            </dd>
          </div>
          {currentClientAccount?.subdomain !== 'sageadmin' && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200'>
              <dt className='text-sm font-medium text-gray-500'>Subscriptions</dt>
              <dd className='mt-1 flex gap-2 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {typeof currentClientAccount?.subscriptions !== 'string' &&
                  currentClientAccount?.subscriptions?.map((sub: string) => <Badge key={sub} badgeType='primary' text={sub} />)}
              </dd>
            </div>
          )}
          {currentClientAccount?.subdomain !== 'sageadmin' && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200'>
              <dt className='text-sm font-medium text-gray-500'>Client Success Manager</dt>
              <dd className='mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <span className='flex-grow'>{currentClientAccount?.sageRep?.name}</span>
              </dd>
            </div>
          )}
          {currentClientAccount?.subdomain !== 'sageadmin' && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200'>
              <dt className='text-sm font-medium text-gray-500'>Client Success Manager Email</dt>
              <dd className='mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <span className='flex-grow'>{currentClientAccount?.sageRep?.email}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}
